import { ErrorCode, errorBodySchema } from '@qaytim/shared';
import type { z } from 'zod';

import { authStore } from './auth-store';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

/** Typed error carrying the machine-readable code; the UI maps it to an Uzbek string. */
export class AdminApiError extends Error {
  constructor(
    readonly code: ErrorCode | string,
    readonly status: number,
    readonly details?: Record<string, unknown>,
  ) {
    super(`${status} ${code}`);
    this.name = 'AdminApiError';
  }
}

export interface RequestOptions<TSchema extends z.ZodTypeAny> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Response contract. Given one, the parsed body is validated before it reaches the UI. */
  schema?: TSchema;
  signal?: AbortSignal;
}

export async function adminRequest<TSchema extends z.ZodTypeAny>(
  path: string,
  options: RequestOptions<TSchema> = {},
): Promise<TSchema extends z.ZodTypeAny ? z.infer<TSchema> : unknown> {
  const { method = 'GET', body, schema, signal } = options;
  const token = authStore.get()?.accessToken;

  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (token) headers.authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch {
    // Network-level failure: no status, no code — surface it as a distinct condition so the UI
    // can say "server unreachable" rather than "unknown error".
    throw new AdminApiError('NETWORK_ERROR', 0);
  }

  if (response.status === 401 || response.status === 403) {
    // An expired admin session is not refreshable (§4.13) — drop it and let the router
    // bounce back to the login page.
    authStore.clear();
  }

  const payload: unknown = response.status === 204 ? null : await response.json().catch(() => null);

  if (!response.ok) {
    const parsed = errorBodySchema.safeParse(payload);
    throw new AdminApiError(
      parsed.success ? parsed.data.code : ErrorCode.INTERNAL_ERROR,
      response.status,
      parsed.success ? parsed.data.details : undefined,
    );
  }

  if (!schema) return payload as never;

  const result = schema.safeParse(payload);
  if (!result.success) {
    // A contract mismatch is a bug, not a user error — fail loudly rather than rendering
    // half-valid data in a moderation console.
    throw new AdminApiError(ErrorCode.VALIDATION_FAILED, response.status);
  }
  return result.data as never;
}

/**
 * Fayl yuklab olish — `<a href>` bilan EMAS.
 *
 * Eksport `Authorization` sarlavhasini talab qiladi, oddiy havola esa uni yubora olmaydi. Yagona
 * muqobil — tokenni URL ga qo'yish, ya'ni uni brauzer tarixiga va server loglariga yozish.
 * Shuning uchun javob `fetch` bilan olinadi va blob sifatida saqlanadi.
 */
export async function adminDownload(path: string, fileName: string): Promise<void> {
  const token = authStore.get()?.accessToken;

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
    });
  } catch {
    throw new AdminApiError('NETWORK_ERROR', 0);
  }

  if (response.status === 401 || response.status === 403) authStore.clear();

  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null);
    const parsed = errorBodySchema.safeParse(payload);
    throw new AdminApiError(
      parsed.success ? parsed.data.code : ErrorCode.INTERNAL_ERROR,
      response.status,
    );
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
